import { FLEET_COST_CATEGORIES, type FleetCostCategory, type InvoiceFleetDto } from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Button, Group, Modal, NumberInput, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconCar, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { can, useMe } from '../../auth';
import { ask } from '../../confirm';
import { Money, MoneyInput } from '../../components/Money';
import { SectionCard } from '../../components/ui';
import { qtyPl } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { useFleetVehicles, vehicleLabel } from './common';

interface Part {
  vehicleId: string | null;
  netGr: number | null;
  liters: number | null;
  note: string;
}

function AllocateModal({ invoiceId, data, initialLp, onClose }: { invoiceId: string; data: InvoiceFleetDto; initialLp: number | null; onClose: () => void }) {
  const qc = useQueryClient();
  const vehicles = useFleetVehicles();
  const [lineLp, setLineLp] = useState<number | null>(initialLp);
  const [category, setCategory] = useState<FleetCostCategory>('paliwo');
  const suggested = data.correctedVehicles.map((v) => ({ vehicleId: v.vehicleId, netGr: null, liters: null, note: '' }));
  const [parts, setParts] = useState<Part[]>(suggested.length ? suggested : [{ vehicleId: null, netGr: null, liters: null, note: '' }]);
  const line = lineLp !== null ? data.lines.find((l) => l.lp === lineLp) : null;
  const base = line ? (line.netGr ?? 0) : data.netGr;
  const already = line ? line.allocatedGr : data.wholeAllocatedGr;
  const entered = parts.reduce((a, p) => a + (p.netGr ?? 0), 0);
  const left = base - already - entered;
  const setPart = (i: number, patch: Partial<Part>) => setParts((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const save = useMutation({
    mutationFn: () =>
      api<InvoiceFleetDto>(`/fleet/invoices/${invoiceId}/allocations`, {
        method: 'POST',
        body: { lineLp, category, parts: parts.map((p) => ({ vehicleId: p.vehicleId, netGr: p.netGr, liters: category === 'paliwo' ? p.liters : null, note: p.note })) },
      }),
    onSuccess: (r) => {
      qc.setQueryData(['fleet', 'invoice', invoiceId], r);
      void qc.invalidateQueries({ queryKey: ['fleet'] });
      notifyOk('Przypisano koszt do pojazdów');
      onClose();
    },
    onError: notifyError,
  });
  const valid = parts.length > 0 && parts.every((p) => p.vehicleId && p.netGr !== null && p.netGr !== 0) && new Set(parts.map((p) => p.vehicleId)).size === parts.length;
  const vehicleData = (vehicles.data ?? []).map((v) => ({ value: v.id, label: vehicleLabel(v) }));
  return (
    <Modal opened onClose={onClose} title="Przypisanie kosztu do pojazdów" size="xl">
      <Stack gap="sm">
        <Group grow align="flex-start">
          <Select
            label="Co przypisać"
            data={[{ value: 'all', label: 'Cała faktura' }, ...data.lines.map((l) => ({ value: String(l.lp), label: `Poz. ${l.lp}: ${l.name}` }))]}
            value={lineLp === null ? 'all' : String(lineLp)}
            onChange={(x) => setLineLp(!x || x === 'all' ? null : Number(x))}
            allowDeselect={false}
          />
          <Select label="Kategoria kosztu" data={Object.entries(FLEET_COST_CATEGORIES).map(([value, label]) => ({ value, label }))} value={category} onChange={(x) => x && setCategory(x as FleetCostCategory)} allowDeselect={false} />
        </Group>
        <Text size="sm">
          Wartość netto: <Money gr={base} /> · już przypisano: <Money gr={already} /> ·{' '}
          <Text span fw={700} c={left < 0 ? 'var(--app-text-red)' : undefined}>
            zostaje do przypisania: <Money gr={left} />
          </Text>
        </Text>
        {data.correctedVehicles.length > 0 && (
          <Alert color="blue" variant="light" py="xs">
            Korekta — podpowiadamy pojazdy z faktury korygowanej. Kwoty korekty mogą być ujemne.
          </Alert>
        )}
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Pojazd</Table.Th>
              <Table.Th w={150}>Netto</Table.Th>
              {category === 'paliwo' && <Table.Th w={110}>Litry</Table.Th>}
              <Table.Th>Opis</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {parts.map((p, i) => (
              <Table.Tr key={i}>
                <Table.Td>
                  <Select searchable placeholder="Wybierz pojazd" data={vehicleData} value={p.vehicleId} onChange={(x) => setPart(i, { vehicleId: x })} />
                </Table.Td>
                <Table.Td>
                  <MoneyInput valueGr={p.netGr} onChangeGr={(gr) => setPart(i, { netGr: gr })} />
                </Table.Td>
                {category === 'paliwo' && (
                  <Table.Td>
                    <NumberInput min={0} decimalScale={2} decimalSeparator="," value={p.liters ?? ''} onChange={(x) => setPart(i, { liters: typeof x === 'number' ? x : null })} />
                  </Table.Td>
                )}
                <Table.Td>
                  <TextInput value={p.note} onChange={(e) => setPart(i, { note: e.currentTarget.value })} />
                </Table.Td>
                <Table.Td>
                  {parts.length > 1 && (
                    <ActionIcon variant="subtle" color="red" aria-label="Usuń wiersz" onClick={() => setParts((ps) => ps.filter((_, j) => j !== i))}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Group justify="space-between">
          <Group gap="xs">
            <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => setParts((ps) => [...ps, { vehicleId: null, netGr: null, liters: null, note: '' }])}>
              Dodaj pojazd
            </Button>
            {left !== 0 && parts.length > 0 && parts[parts.length - 1]!.netGr === null && (
              <Button size="xs" variant="subtle" onClick={() => setPart(parts.length - 1, { netGr: left })}>
                Wpisz pozostałą kwotę
              </Button>
            )}
          </Group>
          {left < 0 && (
            <Text size="sm" c="var(--app-text-red)">
              Suma przekracza wartość {line ? 'pozycji' : 'faktury'}
            </Text>
          )}
        </Group>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
            Przypisz do pojazdów
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Finanse → Faktura zakupu: sekcja „Flota” — przypisanie pozycji lub części kwoty do pojazdów (bez kopiowania faktury). */
export function InvoiceFleetSection({ invoiceId }: { invoiceId: string }) {
  const { data: me } = useMe();
  const enabled = !!me?.features.flota && can(me, 'flota.koszty');
  const qc = useQueryClient();
  const vehicles = useFleetVehicles(enabled);
  const q = useQuery({ queryKey: ['fleet', 'invoice', invoiceId], queryFn: () => api<InvoiceFleetDto>(`/fleet/invoices/${invoiceId}`), enabled: enabled && vehicles.isSuccess, retry: false });
  const [allocating, setAllocating] = useState<number | null | undefined>(undefined);
  const remove = useMutation({
    mutationFn: (id: string) => api(`/fleet/costs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      notifyOk('Usunięto przypisanie');
      void qc.invalidateQueries({ queryKey: ['fleet'] });
    },
    onError: notifyError,
  });
  // brak modułu / uprawnień (np. 403 z listy pojazdów) — sekcja ukryta
  if (!enabled || vehicles.isError || q.isError || !q.data) return null;
  const d = q.data;
  const allocatedTotal = d.wholeAllocatedGr + d.lines.reduce((a, l) => a + l.allocatedGr, 0);
  return (
    <SectionCard
      title="Flota"
      icon={<IconCar size={16} />}
      padding={0}
      actions={
        !d.rejected && (
          <Button size="xs" variant="light" onClick={() => setAllocating(null)}>
            Przypisz do pojazdu
          </Button>
        )
      }
    >
      <Stack gap={0}>
        {d.rejected && (
          <Alert color="gray" variant="light" m="sm" py="xs">
            Faktura odrzucona — jej przypisania nie wchodzą do raportów kosztów floty.
          </Alert>
        )}
        <Text size="sm" px="lg" pb="xs">
          Przypisano do pojazdów: <Money gr={allocatedTotal} /> z <Money gr={d.netGr} /> netto.
        </Text>
        {d.lines.length > 0 && (
          <Table.ScrollContainer minWidth={520}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Pozycja</Table.Th>
                  <Table.Th ta="right">Netto</Table.Th>
                  <Table.Th ta="right">Przypisano</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {d.lines.map((l) => (
                  <Table.Tr key={l.lp}>
                    <Table.Td>
                      <Text size="sm">
                        {l.lp}. {l.name}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Money gr={l.netGr} />
                    </Table.Td>
                    <Table.Td ta="right">{l.allocatedGr ? <Money gr={l.allocatedGr} /> : '—'}</Table.Td>
                    <Table.Td ta="right">
                      {!d.rejected && (l.netGr === null || l.allocatedGr < l.netGr) && (
                        <Button size="compact-sm" variant="subtle" onClick={() => setAllocating(l.lp)}>
                          Przypisz
                        </Button>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {d.allocations.length > 0 && (
          <Table.ScrollContainer minWidth={520}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Pojazd</Table.Th>
                  <Table.Th>Dotyczy</Table.Th>
                  <Table.Th ta="right">Netto</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {d.allocations.map((a) => (
                  <Table.Tr key={a.id}>
                    <Table.Td>
                      <Anchor component={Link} to={`/flota/pojazdy/${a.vehicleId}?zakladka=koszty`} size="sm" fw={600}>
                        {a.plate}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {a.invoice?.lineLp !== null && a.invoice?.lineLp !== undefined ? `poz. ${a.invoice.lineLp}` : 'cała faktura'} · {FLEET_COST_CATEGORIES[a.category]}
                        {a.liters !== null ? ` · ${qtyPl(a.liters)} l` : ''}
                      </Text>
                      {a.note && (
                        <Text size="xs" c="dimmed">
                          {a.note}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Money gr={a.netGr} />
                    </Table.Td>
                    <Table.Td ta="right">
                      <ActionIcon variant="subtle" color="red" aria-label="Usuń przypisanie" onClick={async () => (await ask(`Koszt zniknie z pojazdu ${a.plate}; kwota wróci do puli do przypisania.`, { title: 'Usunąć przypisanie?', confirm: 'Usuń', danger: true })) && remove.mutate(a.id)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
      {allocating !== undefined && <AllocateModal invoiceId={invoiceId} data={d} initialLp={allocating} onClose={() => setAllocating(undefined)} />}
    </SectionCard>
  );
}
